<template>
    <section class="page-select container">
        <!-- Back Button -->
        <BaseButton
            class="button-back"
            title="< Back"
            theme="link"
            @click.native="goBack()"
        />

        <!-- ResourcePicker Button for standard selections -->
        <BaseButton
            v-if="!useRelationSetter"
            class="button-select"
            :title="buttonLabel"
            theme="primary"
            @click.native="openPicker()"
        />

        <!-- RelationSetter for Custom Resource Selection -->
        <div v-if="useRelationSetter && resourceConfig" class="item-selection-container">
            <RelationSetter
                :items="items"
                :value="selectedItems"
                :loading="loadingItems"
                :unselectedLabel="resourceConfig.unselectedLabel"
                :selectedLabel="resourceConfig.selectedLabel"
                @input="handleItemSelection"
                key_uid="id"
                key_label="title"
                key_img="image"
                key_img_second="src"
            />

            <BaseButton
                v-if="selectedItems.length > 0"
                class="button-proceed"
                title="Proceed to Edit"
                theme="primary"
                @click.native="proceedWithSelection()"
            />
        </div>
    </section>
</template>

<script>
import RelationSetter from '@/components/RelationSetter'

export default {
    components: {
        RelationSetter
    },
    data() {
        return {
            typeLabel: '',
            resourceType: null,
            items: [],
            selectedItems: [],
            loadingItems: false
        }
    },
    computed: {
        buttonLabel() {
            return `Select ${this.typeLabel} to edit`
        },
        resourceConfig() {
            const configs = {
                page: {
                    endpoint: 'item/page',
                    unselectedLabel: 'Available pages',
                    selectedLabel: 'Selected page'
                },
                blog: {
                    endpoint: 'item/blog',
                    unselectedLabel: 'Available blogs',
                    selectedLabel: 'Selected blog'
                },
                article: {
                    endpoint: 'item/articles',
                    unselectedLabel: 'Available articles',
                    selectedLabel: 'Selected article'
                }
            }
            return configs[this.resourceType] || null
        },
        useRelationSetter() {
            return ['page', 'blog', 'article'].includes(this.resourceType)
        }
    },
    async mounted() {
        let resourceTypeLabel = ''

        if (this.$route.query.redirect) {
            switch (this.$route.query.redirect) {
                case '/products/edit':
                    this.resourceType = 'product'
                    resourceTypeLabel = 'product'
                    break
                case '/collections/edit':
                    this.resourceType = 'collection'
                    resourceTypeLabel = 'collection'
                    break
                case '/variants/edit':
                    this.resourceType = 'variant'
                    resourceTypeLabel = 'variant'
                    break
                case '/pages/edit':
                    this.resourceType = 'page'
                    resourceTypeLabel = 'page'
                    break
                case '/blogs/edit':
                    this.resourceType = 'blog'
                    resourceTypeLabel = 'blog'
                    break
                case '/articles/edit':
                    this.resourceType = 'article'
                    resourceTypeLabel = 'article'
                    break
                default:
                    this.resourceType = 'product'
                    resourceTypeLabel = 'product'
            }
        }

        this.typeLabel = resourceTypeLabel

        this.$store.commit('banners/SHOW_BANNER', {
            title: `Select ${resourceTypeLabel}`,
            message: `Please select a ${resourceTypeLabel} again to edit metafields. You are seeing this screen due to an unexpected problem with Shopify that is not related to Airfields app.`,
            status: 'warning'
        })

        // For RelationSetter types (page, blog, article), fetch items
        if (this.useRelationSetter) {
            this.fetchItems()
        }
    },
    methods: {
        async openPicker() {
            if (!window.shopify) {
                console.error('window.shopify is not available')
                return
            }

            try {
                // For variant selection, use 'product' type with variant selection enabled
                const pickerConfig = {
                    type: this.resourceType === 'variant' ? 'product' : this.resourceType,
                    multiple: false
                }

                // Enable variant selection for variant route
                if (this.resourceType === 'variant') {
                    pickerConfig.action = 'select'
                }

                const selection = await window.shopify.resourcePicker(pickerConfig)

                if (selection && selection.length > 0) {
                    const selectedResource = selection[0]
                    let resourceId = null

                    // Handle variant selection specifically
                    if (this.resourceType === 'variant') {
                        // Check if user actually selected a variant, not just a product
                        if (!selectedResource.variants || selectedResource.variants.length === 0) {
                            // Show error banner - user must select a variant
                            this.$store.commit('banners/SHOW_BANNER', {
                                title: 'Variant Required',
                                message: 'Please select a specific variant from the product, not just the product itself. Click "Select variant to edit" again and choose a variant option.',
                                status: 'critical'
                            })
                            return
                        }

                        // Extract variant ID from the variants array
                        const selectedVariant = selectedResource.variants[0]
                        const regex = /\/(\d+)$/
                        const match = selectedVariant.id.match(regex)

                        if (match && match[1]) {
                            resourceId = match[1]
                        }
                    } else {
                        // For product, collection, etc. - extract ID normally
                        const regex = /\/(\d+)$/
                        const match = selectedResource.id.match(regex)

                        if (match && match[1]) {
                            resourceId = match[1]
                        }
                    }

                    // Proceed with redirect if we have a valid resource ID
                    if (resourceId) {
                        const redirect = this.$route.query.redirect
                        const shop = this.$route.query.shop

                        this.$router.push({
                            path: redirect,
                            query: {
                                id: resourceId,
                                shop
                            }
                        })
                    }
                }
            } catch (error) {
                console.error('ResourcePicker error:', error)
            }
        },
        async fetchItems() {
            if (!this.resourceConfig) return

            this.loadingItems = true
            try {
                const response = await this.$store.dispatch('shopifyApi/REQUEST', {
                    endpoint: this.resourceConfig.endpoint
                })
                this.items = response.items || []
            } catch (error) {
                this.items = []
            } finally {
                this.loadingItems = false
            }
        },
        handleItemSelection(items) {
            this.selectedItems = items
        },
        proceedWithSelection() {
            if (this.selectedItems.length > 0) {
                const selectedItem = this.selectedItems[0]
                const redirect = this.$route.query.redirect
                const shop = this.$route.query.shop

                this.$router.push({
                    path: redirect,
                    query: {
                        id: selectedItem.id,
                        shop
                    }
                })
            }
        },
        goBack() {
            this.$router.go(-1)
        }
    }
}
</script>

<style lang="scss">
@import './src/assets/styles/grid.scss';
@import './src/assets/styles/breakpoints.scss';

.page-select {
    display: flex;
    flex-direction: column;
    align-items: center;

    .button-back {
        margin-bottom: 20px;
        align-self: flex-start;
    }

    .button-select {
        width: 180px;
        height: 50px;
    }

    .item-selection-container {
        width: 100%;
        max-width: 800px;

        .button-proceed {
            margin-top: 20px;
            width: 180px;
            height: 50px;
            display: block;
            margin-left: auto;
            margin-right: auto;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/page-select.vue